'use client'

import React from 'react'
import dynamic from 'next/dynamic'
import { motion } from 'framer-motion'
import type { RenderedService } from '@/types/interfaces/service-item'
import { textFadeInVariantsNoTransition } from '@/lib/animations'
import { useModal } from '@/hooks/useModal'
import Background, { type CategoryHeroBackgroundKey } from './Background'
import Phone, { type CategoryHeroPhoneKey } from './Phone'
import SeeHowItWorksButton from './SeeHowItWorksButton'
import classes from './CategoryHero.module.css'

const ModalManager = dynamic(() => import('@/components/Refresh/Modals/ModalManager/ModalManager'))

const BACKGROUND_KEYS = new Set<CategoryHeroBackgroundKey>(['visits', 'labs', 'medications', 'specialists'])
const PHONE_KEYS = new Set<CategoryHeroPhoneKey>(['visits', 'labs', 'medications', 'specialists'])

const VIDEO_EXTENSION = /\.(mp4|webm|m3u8|mov|m4v)(\?|$)/i
const VIDEO_HOST = /(youtube\.com|youtu\.be|vimeo\.com|player\.vimeo\.com)/i

function isVideoHref(href: string): boolean {
    const trimmed = href.trim()
    if (!trimmed) return false

    if (VIDEO_EXTENSION.test(trimmed)) return true

    try {
        const url = new URL(trimmed, 'https://example.invalid')
        return VIDEO_HOST.test(url.hostname)
    } catch {
        return false
    }
}

export interface CategoryHeroProps {
    categoryHero: RenderedService
}

type CategoryHeroPayload = RenderedService & {
    pageName?: string | null
    background?: string | null
    phone?: string | null
    href?: string | null
}

function resolveBackgroundKey(value: string | null | undefined): CategoryHeroBackgroundKey | null {
    if (value == null) return null
    return BACKGROUND_KEYS.has(value as CategoryHeroBackgroundKey) ? (value as CategoryHeroBackgroundKey) : null
}

function resolvePhoneKey(value: string | null | undefined): CategoryHeroPhoneKey | null {
    if (value == null) return null
    return PHONE_KEYS.has(value as CategoryHeroPhoneKey) ? (value as CategoryHeroPhoneKey) : null
}

/**
 * Category page hero: blurred background, left copy + optional “See how it works” CTA,
 * and a keyed phone mockup on the right (stacked under copy on mobile).
 */
export default function CategoryHero({ categoryHero }: CategoryHeroProps) {
    const payload = categoryHero as CategoryHeroPayload
    const pageName = payload.pageName?.trim() || null
    const title = payload.title?.trim() || null
    const subtitle = payload.subtitle?.trim() || null
    const href = payload.href?.trim() || null
    const background = resolveBackgroundKey(payload.background)
    const phone = resolvePhoneKey(payload.phone)

    const videoModal = useModal()
    const opensVideo = href != null && isVideoHref(href)

    if (!pageName && !title && !subtitle && background == null && phone == null) return null

    return (
        <>
            {opensVideo && href && <ModalManager type="video" {...videoModal} videoUrl={href} />}
            <div className={classes.root} data-category-hero={categoryHero.id}>
                {/* Background + phone paint immediately so they are not held behind opacity for LCP. */}
                {background != null ? <Background background={background} /> : null}
                <div className={classes.content}>
                    <motion.div
                        className={classes.textColumn}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-80px 0px' }}
                        variants={textFadeInVariantsNoTransition}
                    >
                        {pageName ? <p className={classes.pageName}>{pageName}</p> : null}
                        <div className={classes.titleBlock}>
                            {title ? <h1 className={classes.title}>{title}</h1> : null}
                            <div className={classes.subtitleBlock}>
                                {subtitle ? <p className={classes.subtitle}>{subtitle}</p> : null}
                                {href ? (
                                    <SeeHowItWorksButton
                                        href={href}
                                        opensVideo={opensVideo}
                                        onVideoClick={videoModal.open}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </motion.div>
                    {phone != null ? (
                        <div className={classes.phoneWrap}>
                            <Phone phone={phone} />
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    )
}

export type { CategoryHeroBackgroundKey } from './Background'
export type { CategoryHeroPhoneKey } from './Phone'
